import React, { useEffect, useRef, useState } from 'react';
import { Form, Select, InputNumber, Button, Spin, Typography, theme, Tag, Alert, Segmented } from 'antd';
import { message } from '../../services/toastMessage';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconBriefcase, IconMapPin, IconCoin, IconStar, IconX } from '@tabler/icons-react';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import FloatingLabelAutoComplete from '../../components/FloatingLabelAutoComplete';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import StaticMap from '../../components/StaticMap';
import EmojiTextArea from '../../components/EmojiTextArea';
import WizardTileGrid from '../../components/WizardTileGrid';
import TaxonomyPicker from '../../components/TaxonomyPicker';
import WizardIllustration from '../../components/WizardIllustration';
import {
  getServiceById,
  createService,
  updateService,
  addServiceMachineryCompatibility,
  removeServiceMachineryCompatibility,
  createServiceAddOn,
  updateServiceAddOn,
  NO_SERVICE_PROVIDER_PROFILE_ERROR,
} from '../../services/ServiceService';
import { IService, IServiceAddOn } from '../../interfaces/heavyDutyMachinery/IService';
import { getCountries, ICountryResponse } from '../../services/LocaleService';
import {
  SERVICE_CATEGORY_OPTIONS,
  SERVICE_CATEGORY_SECTORS,
  PRICING_MODE_OPTIONS,
  OPERATOR_TRADE_SLUGS_WITHOUT_ATTACHMENT,
  getServiceCategoryBySlug,
  getServiceCategoryById,
} from '../../constants/serviceOptions';
import { getMachineryBrandsForCategory, getMachineryModelsForBrand } from '../../constants/machineryBrandsSeed';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { WIZARD_TONE_EMERALD, WIZARD_TONE_OCHRE, WIZARD_TONE_PINE, WIZARD_TONE_TERRACOTTA } from '../../theme/creamyTokens';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';

const { Text } = Typography;

interface ServiceFormValues {
  categorySlug: string;
  title: string;
  description?: string;
  countryCode: string;
  hostCurrencyCode: string;
  pricingMode: string;
  requestMode: string;
  hourlyRate?: number;
  fixedJobPrice?: number;
  depositAmount?: number;
  // Public approximate location -- same shape as HeavyDutyMachineryForm's
  // own city/province/region/formattedAddress/administrativeLevel/country/
  // latitude/longitude, captured via LocationAutocomplete in the 'location'
  // phase below. Deliberately NOT wired into countryCode above (unlike
  // Machinery, which derives its one countryCode from the address pick
  // itself) -- Service already had its own manual country Select for
  // currency purposes before location existed, and reusing the same field
  // name here would silently fight that picker on every address change.
  city?: string;
  province?: string;
  region?: string;
  formattedAddress?: string;
  administrativeLevel?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

type PhaseKey = 'category' | 'location' | 'pricing' | 'extras';

interface PhaseConfig {
  key: PhaseKey;
  titleKey: string;
  subtitleKey: string;
  icon: typeof IconBriefcase;
  background: string;
}

// Same one-dark-background-per-phase convention as HeavyDutyMachineryForm's
// own PHASES -- purely a visual cue, no other meaning attached.
const PHASES: PhaseConfig[] = [
  { key: 'category', titleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_CATEGORY_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_CATEGORY_SUBTITLE, icon: IconBriefcase, background: WIZARD_TONE_EMERALD },
  { key: 'location', titleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_LOCATION_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_LOCATION_SUBTITLE, icon: IconMapPin, background: WIZARD_TONE_OCHRE },
  { key: 'pricing', titleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_PRICING_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_PRICING_SUBTITLE, icon: IconCoin, background: WIZARD_TONE_PINE },
  { key: 'extras', titleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_EXTRAS_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_SERVICE_EXTRAS_SUBTITLE, icon: IconStar, background: WIZARD_TONE_TERRACOTTA },
];

// A Service's own sub-resources (ServiceMachineryCompatibility, ServiceAddOn)
// only ever exist against an already-created Service (POST .../{id}/...),
// so the 'extras' phase's fields aren't part of form validation the way
// category/pricing are -- there's nothing to validate client-side there,
// leaving the sub-resource forms below to validate themselves inline.
const getFieldsForPhase = (phaseKey: PhaseKey, pricingMode?: string): (keyof ServiceFormValues)[] => {
  switch (phaseKey) {
    case 'category':
      return ['categorySlug'];
    case 'location':
      return ['city'];
    case 'pricing': {
      const fields: (keyof ServiceFormValues)[] = ['title', 'countryCode', 'pricingMode'];
      if (pricingMode === 'Hourly' || pricingMode === 'Both') fields.push('hourlyRate');
      if (pricingMode === 'FixedJob' || pricingMode === 'Both') fields.push('fixedJobPrice');
      return fields;
    }
    default:
      return [];
  }
};

const fireSaveConfetti = () => {
  confetti({ particleCount: 50, spread: 60, startVelocity: 28, scalar: 0.85, origin: { x: 0.16, y: 0.35 }, colors: ['#bfe9ff', '#ffffff', '#3ba7d9'] });
};

// Same fixed caption-above-pill-input treatment as HeavyDutyMachineryForm's
// own PillNumberField -- FloatingLabelInput/Number's floating label assumes
// a single line, which this wizard's longer field names don't always fit.
const PillNumberField: React.FC<{
  label: string;
  min?: number;
  step?: number;
  addonAfter?: string;
  disabled?: boolean;
  value?: number;
  onChange?: (value: number | null) => void;
}> = ({ label, value, onChange, addonAfter, ...rest }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
      {label}
    </Text>
    <InputNumber
      {...rest}
      addonAfter={addonAfter}
      className={addonAfter ? 'wizard-currency-input' : 'wizard-number-input'}
      style={{ width: '100%' }}
      value={value}
      onChange={onChange}
      inputMode="decimal"
    />
  </div>
);

const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 40 }}>
    {!!label && (
      <Text strong style={{ display: 'block', fontSize: 20, marginBottom: 16 }}>
        {label}
      </Text>
    )}
    {children}
  </div>
);

/**
 * A provider's own management of which Machinery (category/brand/model) a
 * Service is compatible with -- only rendered when the selected
 * ServiceCategory has attachableToMachineryBooking (see ServiceCategory's
 * own doc comment). Lives inside the wizard's last phase rather than as a
 * separate screen, same "post-save section revealed once the parent record
 * exists" pattern as MyOperators' own document upload section, since
 * POST .../compatibilities needs an already-created Service id.
 */
const ServiceCompatibilityManager: React.FC<{ service: IService; onChange: (service: IService) => void; disabled?: boolean }> = ({
  service,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const { resolveName: categoryLabel } = useTaxonomy();
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [brand, setBrand] = useState<string | undefined>(undefined);
  const [model, setModel] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);

  const brandSuggestions = getMachineryBrandsForCategory(category);
  const modelSuggestions = getMachineryModelsForBrand(category, brand);

  const handleAdd = async () => {
    if (!category) return;
    setAdding(true);
    try {
      const created = await addServiceMachineryCompatibility(service.id, {
        machineryCategory: category,
        brand: brand?.trim() || undefined,
        model: model?.trim() || undefined,
      });
      onChange({ ...service, machineryCompatibilities: [...service.machineryCompatibilities, created] });
      setCategory(undefined);
      setBrand(undefined);
      setModel(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_ADD_ERROR));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (compatibilityId: string) => {
    try {
      await removeServiceMachineryCompatibility(service.id, compatibilityId);
      onChange({ ...service, machineryCompatibilities: service.machineryCompatibilities.filter((c) => c.id !== compatibilityId) });
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_REMOVE_ERROR));
    }
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <Text strong style={{ display: 'block', fontSize: 18, marginBottom: 4 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_TITLE)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_HINT)}
      </Text>

      {service.machineryCompatibilities.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_EMPTY)}
        </Text>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {service.machineryCompatibilities.map((c) => (
            <Tag
              key={c.id}
              closable={!disabled}
              onClose={(e) => {
                e.preventDefault();
                handleRemove(c.id);
              }}
              style={{ borderRadius: 999, padding: '4px 10px' }}
            >
              {categoryLabel(c.machineryCategory)}
              {c.brand ? ` · ${c.brand}` : ` · ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_ANY_BRAND)}`}
              {c.model ? ` ${c.model}` : ''}
            </Tag>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220 }}>
          <TaxonomyPicker
            value={category ?? ''}
            onChange={(value) => {
              setCategory(value as string);
              setBrand(undefined);
              setModel(undefined);
            }}
            placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_CATEGORY_LABEL)}
            disabled={disabled || adding}
          />
        </div>
        <FloatingLabelAutoComplete
          label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_BRAND_LABEL)}
          value={brand}
          onChange={setBrand}
          options={brandSuggestions}
          disabled={disabled || adding || !category}
          style={{ minWidth: 160 }}
        />
        <FloatingLabelAutoComplete
          label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_MODEL_LABEL)}
          value={model}
          onChange={setModel}
          options={modelSuggestions}
          disabled={disabled || adding || !brand}
          style={{ minWidth: 160 }}
        />
        <Button shape="round" onClick={handleAdd} loading={adding} disabled={disabled || !category}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_ADD_BUTTON)}
        </Button>
      </div>
    </div>
  );
};

/**
 * Create/pause/edit the priced extras a guest can bundle into booking this
 * Service -- same POST .../add-ons already built on the backend
 * (ServiceController), no hard-delete endpoint (pausing via active=false is
 * how a provider retires one). Same "needs an existing Service id, lives in
 * the last phase" reasoning as ServiceCompatibilityManager above.
 */
const ServiceAddOnManager: React.FC<{ service: IService; onChange: (service: IService) => void; disabled?: boolean }> = ({
  service,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState<number | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const handleAdd = async () => {
    if (!newTitle.trim() || !newPrice || newPrice <= 0) return;
    setAdding(true);
    try {
      const created = await createServiceAddOn(service.id, { title: newTitle.trim(), price: newPrice });
      onChange({ ...service, addOns: [...service.addOns, created] });
      setNewTitle('');
      setNewPrice(null);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_SAVE_ERROR));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (addOn: IServiceAddOn) => {
    setEditingId(addOn.id);
    setEditTitle(addOn.title);
    setEditPrice(addOn.price);
  };

  const saveEdit = async (addOnId: string) => {
    if (!editTitle.trim() || !editPrice || editPrice <= 0) return;
    setSavingEdit(true);
    try {
      const updated = await updateServiceAddOn(service.id, addOnId, { title: editTitle.trim(), price: editPrice });
      onChange({ ...service, addOns: service.addOns.map((a) => (a.id === updated.id ? updated : a)) });
      setEditingId(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_SAVE_ERROR));
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleActive = async (addOn: IServiceAddOn) => {
    try {
      const updated = await updateServiceAddOn(service.id, addOn.id, { active: !addOn.active });
      onChange({ ...service, addOns: service.addOns.map((a) => (a.id === updated.id ? updated : a)) });
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_SAVE_ERROR));
    }
  };

  return (
    <div>
      <Text strong style={{ display: 'block', fontSize: 18, marginBottom: 4 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ONS_TITLE)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ONS_HINT)}
      </Text>

      {service.addOns.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_EMPTY)}
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {service.addOns.map((addOn) => (
            <div
              key={addOn.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 12px',
                borderRadius: 14,
                background: 'rgba(0,0,0,0.03)',
                opacity: addOn.active ? 1 : 0.6,
                flexWrap: 'wrap',
              }}
            >
              {editingId === addOn.id ? (
                <>
                  <FloatingLabelInput
                    label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_TITLE_LABEL)}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    style={{ flex: 1, minWidth: 140 }}
                  />
                  <InputNumber min={0.01} step={0.01} value={editPrice ?? undefined} onChange={(v) => setEditPrice(v)} style={{ width: 120 }} />
                  <Button size="small" type="primary" shape="round" loading={savingEdit} onClick={() => saveEdit(addOn.id)}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_SAVE_BUTTON)}
                  </Button>
                  <Button size="small" shape="round" onClick={() => setEditingId(undefined)}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_CANCEL_BUTTON)}
                  </Button>
                </>
              ) : (
                <>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <Text strong style={{ display: 'block', fontSize: 13 }}>
                      {addOn.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {addOn.price.toFixed(2)} {service.hostCurrencyCode}
                    </Text>
                  </div>
                  <Button size="small" shape="round" onClick={() => startEdit(addOn)} disabled={disabled}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_EDIT_BUTTON)}
                  </Button>
                  <Button size="small" shape="round" onClick={() => toggleActive(addOn)} disabled={disabled}>
                    {translate(
                      t,
                      addOn.active ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_PAUSE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_RESUME_BUTTON
                    )}
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FloatingLabelInput
          label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_TITLE_LABEL)}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          style={{ minWidth: 180, flex: 1 }}
          disabled={disabled || adding}
        />
        <InputNumber
          min={0.01}
          step={0.01}
          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_PRICE_LABEL)}
          value={newPrice ?? undefined}
          onChange={(v) => setNewPrice(v)}
          style={{ width: 140 }}
          disabled={disabled || adding}
        />
        <Button shape="round" onClick={handleAdd} loading={adding} disabled={disabled || !newTitle.trim() || !newPrice}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_ADD_BUTTON)}
        </Button>
      </div>
    </div>
  );
};

const ServiceForm: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<ServiceFormValues>();
  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Set once the Service actually exists (either loaded via :id, or just
  // created by performSave below) -- ServiceCompatibilityManager/
  // ServiceAddOnManager in the 'extras' phase need a real id to call their
  // own POST endpoints against, so that phase renders nothing until this is set.
  const [savedService, setSavedService] = useState<IService | undefined>(undefined);
  const effectiveServiceId = id ?? savedService?.id;

  const [countries, setCountries] = useState<ICountryResponse[]>([]);
  // categorySlug's own Form.Item lives in the 'category' phase only, but the
  // deposit note (rendered in 'pricing') and the compatibility manager
  // (rendered in 'extras') both need to know it -- same onValuesChange-into-
  // useState pattern HeavyDutyMachineryForm uses for rentalMode/hasWetHire,
  // since Form.useWatch stops reflecting a field once its own Form.Item unmounts.
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | undefined>(undefined);
  const pricingMode: string | undefined = Form.useWatch('pricingMode', form);
  const hostCurrencyCode: string | undefined = Form.useWatch('hostCurrencyCode', form);
  const selectedLatitude: number | undefined = Form.useWatch('latitude', form);
  const selectedLongitude: number | undefined = Form.useWatch('longitude', form);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [phaseIndex]);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    getServiceById(id)
      .then((service) => {
        setSavedService(service);
        const categoryOption = getServiceCategoryById(service.serviceCategoryId);
        setSelectedCategorySlug(categoryOption?.slug);
        form.setFieldsValue({
          categorySlug: categoryOption?.slug,
          title: service.title,
          description: service.description,
          countryCode: service.countryCode,
          hostCurrencyCode: service.hostCurrencyCode,
          pricingMode: service.pricingMode,
          requestMode: service.requestMode ?? 'Instant',
          hourlyRate: service.hourlyRate,
          fixedJobPrice: service.fixedJobPrice,
          depositAmount: service.depositAmount,
          city: service.city,
          province: service.province,
          region: service.region,
          formattedAddress: service.formattedAddress,
          administrativeLevel: service.administrativeLevel,
          country: service.country,
          latitude: service.latitude,
          longitude: service.longitude,
        });
      })
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_NOT_FOUND));
        navigate('/heavy-duty-machinery/services');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Preselects hostCurrencyCode from the chosen country's own currency, only
  // while the provider hasn't overridden it by hand -- same
  // lastAutoCurrencyRef guard as HeavyDutyMachineryForm's own effect.
  const lastAutoCurrencyRef = useRef<string | undefined>(undefined);
  const handleCountryChange = (countryCode: string) => {
    const currencyCode = countries.find((c) => c.alpha2Code === countryCode)?.currencyCode;
    if (!currencyCode) return;
    const currentValue = form.getFieldValue('hostCurrencyCode');
    if (!currentValue || currentValue === lastAutoCurrencyRef.current) {
      form.setFieldsValue({ hostCurrencyCode: currencyCode });
      lastAutoCurrencyRef.current = currencyCode;
    }
  };

  const countryOptions = countries
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({ value: c.alpha2Code, label: c.name }));

  // Deduplicated by currencyCode, same as HeavyDutyMachineryForm's own currencyOptions.
  const currencyOptions = Array.from(new Map(countries.map((c) => [c.currencyCode, c])).values())
    .filter((c) => !!c.currencyCode)
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
    .map((c) => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currency}` }));

  // A plain searchable Select, not WizardTileGrid -- ~60 trades is too many
  // for a tile grid (see this catalog's own doc comment for why there are
  // no per-category icons either). The 4 original categories (no sectorKey)
  // render as flat top-level options; everything else groups under its own
  // sector as an antd Select optgroup.
  const ungroupedCategoryOptions = SERVICE_CATEGORY_OPTIONS
    .filter((option) => !option.sectorKey)
    .map((option) => ({ value: option.slug, label: translate(t, option.labelKey) }));
  const categoryOptions = [
    ...ungroupedCategoryOptions,
    ...SERVICE_CATEGORY_SECTORS.map((sector) => ({
      label: translate(t, sector.labelKey),
      options: SERVICE_CATEGORY_OPTIONS
        .filter((option) => option.sectorKey === sector.key)
        .map((option) => ({ value: option.slug, label: translate(t, option.labelKey) })),
    })),
  ];

  const requiredRule = { required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) };
  const numericFieldItemStyle: React.CSSProperties = isMobile ? { width: '100%' } : { flex: 1, minWidth: 180 };

  const currentPhase = PHASES[phaseIndex];
  const isLastPhase = phaseIndex === PHASES.length - 1;
  const selectedCategoryOption = getServiceCategoryBySlug(selectedCategorySlug);

  // The actual create/update call -- deliberately happens leaving 'pricing'
  // (not on a final "Publicar" at the end): ServiceMachineryCompatibility/
  // ServiceAddOn both need an existing Service id (see the two managers
  // above), so the Service itself must be persisted before the 'extras'
  // phase has anything to manage. Unlike HeavyDutyMachineryForm's own
  // handleSubmit, this never navigates away on success -- it stays on this
  // same page/URL so the freshly created id is available to 'extras'
  // immediately, same "modal stays open on the now-saved record" reasoning
  // as MyOperators' own document section.
  const performSave = async (): Promise<boolean> => {
    const values = form.getFieldsValue(true) as ServiceFormValues;
    const categoryOption = getServiceCategoryBySlug(values.categorySlug);
    if (!categoryOption) return false;

    const isFirstSave = !effectiveServiceId;
    setSubmitting(true);
    try {
      const payload = {
        serviceCategoryId: categoryOption.id,
        pricingMode: values.pricingMode,
        requestMode: values.requestMode,
        hourlyRate: values.hourlyRate,
        fixedJobPrice: values.fixedJobPrice,
        countryCode: values.countryCode,
        hostCurrencyCode: values.hostCurrencyCode,
        title: values.title,
        description: values.description,
        city: values.city,
        province: values.province,
        region: values.region,
        formattedAddress: values.formattedAddress,
        administrativeLevel: values.administrativeLevel,
        country: values.country,
        latitude: values.latitude,
        longitude: values.longitude,
        depositAmount: values.depositAmount,
      };
      const saved = effectiveServiceId ? await updateService(effectiveServiceId, payload) : await createService(payload);
      setSavedService(saved);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_SAVE_SUCCESS));
      if (isFirstSave) fireSaveConfetti();
      return true;
    } catch (error: any) {
      if (error.message === NO_SERVICE_PROVIDER_PROFILE_ERROR) {
        // Nothing productive left to do in this wizard without a profile --
        // same "take them straight to the fix" spirit as MyServices' own
        // noProfile EmptyState action button.
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_NO_PROVIDER_PROFILE_ERROR));
        navigate('/service-provider/onboarding');
      } else {
        message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_SAVE_ERROR));
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    try {
      await form.validateFields(getFieldsForPhase(currentPhase.key, pricingMode));
    } catch {
      return;
    }
    if (currentPhase.key === 'pricing') {
      const ok = await performSave();
      if (!ok) return;
    }
    setPhaseIndex((i) => Math.min(i + 1, PHASES.length - 1));
  };

  const goBack = () => setPhaseIndex((i) => Math.max(i - 1, 0));

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: isMobile ? '100dvh' : '100vh', overflow: 'hidden' }}>
      {!isMobile && (
        <div
          style={{
            width: '38%',
            minWidth: 320,
            maxWidth: 460,
            flexShrink: 0,
            background: currentPhase.background,
            color: '#fff',
            padding: '48px 40px 40px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: 'background-color 400ms ease',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPhase.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {t(TRANSLATIONS.WIZARD_STEP_LABEL, { current: phaseIndex + 1, total: PHASES.length })}
              </Text>
              <h1 style={{ color: '#fff', fontSize: 30, margin: '8px 0 12px' }}>{translate(t, currentPhase.titleKey)}</h1>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 1.5 }}>{translate(t, currentPhase.subtitleKey)}</Text>
            </motion.div>
          </AnimatePresence>

          <div style={{ display: 'flex', gap: 6, marginTop: 32 }}>
            {PHASES.map((phase, i) => (
              <div
                key={phase.key}
                style={{
                  height: 4,
                  borderRadius: 2,
                  flex: i === phaseIndex ? 2.4 : 1,
                  background: i <= phaseIndex ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)',
                  transition: 'flex 300ms ease, background-color 300ms ease',
                }}
              />
            ))}
          </div>

          <div style={{ marginTop: 'auto' }}>
            <WizardIllustration icon={currentPhase.icon} phaseKey={currentPhase.key} />
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {isMobile ? (
          <div style={{ padding: '16px 20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <Text style={{ color: token.colorTextTertiary, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {t(TRANSLATIONS.WIZARD_STEP_LABEL, { current: phaseIndex + 1, total: PHASES.length })}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
                onClick={() => navigate('/heavy-duty-machinery/services')}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: token.colorTextTertiary, padding: 11, flexShrink: 0 }}
              >
                <IconX size={22} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <h1 style={{ fontSize: 20, margin: '6px 0 4px' }}>{translate(t, currentPhase.titleKey)}</h1>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                  {translate(t, currentPhase.subtitleKey)}
                </Text>
              </motion.div>
            </AnimatePresence>

            <div style={{ display: 'flex', gap: 4, marginTop: 12, marginBottom: 4 }}>
              {PHASES.map((phase, i) => (
                <div
                  key={phase.key}
                  style={{
                    height: 4,
                    borderRadius: 2,
                    flex: i === phaseIndex ? 2.4 : 1,
                    background: i <= phaseIndex ? token.colorPrimary : token.colorFillTertiary,
                    transition: 'flex 300ms ease, background-color 300ms ease',
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '20px 40px 0' }}>
            <button
              type="button"
              aria-label={translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
              onClick={() => navigate('/heavy-duty-machinery/services')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: token.colorTextTertiary, padding: 11 }}
            >
              <IconX size={22} />
            </button>
          </div>
        )}

        <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '8px 20px 24px' : '8px 56px 32px' }}>
          <Form
            form={form}
            layout="vertical"
            component={false}
            onValuesChange={(changedValues) => {
              if ('categorySlug' in changedValues) setSelectedCategorySlug(changedValues.categorySlug);
              if ('countryCode' in changedValues) handleCountryChange(changedValues.countryCode);
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase.key}
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ maxWidth: 640 }}
              >
                {currentPhase.key === 'category' && (
                  <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_CATEGORY_LABEL)}>
                    <Form.Item name="categorySlug" rules={[requiredRule]}>
                      <Select
                        showSearch
                        disabled={submitting}
                        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_CATEGORY_LABEL)}
                        filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                        options={categoryOptions}
                      />
                    </Form.Item>
                    {/* The wrong door, caught at the moment it is opened --
                        see OPERATOR_TRADE_SLUGS_WITHOUT_ATTACHMENT for why
                        these trades exist and why they are still not
                        attachable. Said here rather than left for the
                        provider to work out from an empty calendar months
                        later, and worded as a question rather than a
                        correction: hiring for a crane already standing on a
                        site is a real job, and picking this on purpose is a
                        perfectly good answer. */}
                    {OPERATOR_TRADE_SLUGS_WITHOUT_ATTACHMENT.includes(selectedCategorySlug ?? '') && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={translate(t, TRANSLATIONS.SERVICE_CATEGORY_OPERATOR_HINT_TITLE)}
                        description={translate(t, TRANSLATIONS.SERVICE_CATEGORY_OPERATOR_HINT_BODY)}
                      />
                    )}
                  </FieldGroup>
                )}

                {currentPhase.key === 'location' && (
                  <FieldGroup label="">
                    <Form.Item
                      name="city"
                      rules={[
                        requiredRule,
                        {
                          validator: () =>
                            form.getFieldValue('latitude') != null
                              ? Promise.resolve()
                              : Promise.reject(new Error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_NOT_SELECTED))),
                        },
                      ]}
                    >
                      <LocationAutocomplete
                        disabled={submitting}
                        includeCountryInLabel
                        floatingLabel={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_LABEL)}
                        onSelectLocation={(location) => {
                          form.setFieldsValue({
                            province: location.province,
                            region: location.region,
                            country: location.country,
                            formattedAddress: location.formattedAddress,
                            administrativeLevel: location.administrativeLevel,
                            latitude: location.latitude,
                            longitude: location.longitude,
                          });
                        }}
                      />
                    </Form.Item>
                    {selectedLatitude != null && selectedLongitude != null && (
                      <div style={{ marginTop: 8 }}>
                        <StaticMap latitude={selectedLatitude} longitude={selectedLongitude} />
                      </div>
                    )}
                    <Form.Item name="province" hidden><input /></Form.Item>
                    <Form.Item name="region" hidden><input /></Form.Item>
                    <Form.Item name="country" hidden><input /></Form.Item>
                    <Form.Item name="formattedAddress" hidden><input /></Form.Item>
                    <Form.Item name="administrativeLevel" hidden><input /></Form.Item>
                    <Form.Item name="latitude" hidden><input /></Form.Item>
                    <Form.Item name="longitude" hidden><input /></Form.Item>
                  </FieldGroup>
                )}

                {currentPhase.key === 'pricing' && (
                  <>
                    <FieldGroup label="">
                      <Form.Item name="title" rules={[requiredRule]} style={{ marginBottom: 20 }}>
                        <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_TITLE_LABEL)} maxLength={200} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DESCRIPTION_LABEL)}>
                      <Form.Item name="description">
                        <EmojiTextArea
                          rows={4}
                          maxLength={2000}
                          disabled={submitting}
                          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DESCRIPTION_PLACEHOLDER)}
                        />
                      </Form.Item>
                    </FieldGroup>

                    {/* How a customer gets to a price here, and the one
                        setting on this form that changes what they SEE: under
                        "pide presupuesto" no calendar is shown at all.
                        Deliberately outside the pricing block -- it is not a
                        third pricing mode, it is a different conversation. */}
                    <FieldGroup label={translate(t, TRANSLATIONS.SERVICE_FORM_REQUEST_MODE_LABEL)}>
                      <Form.Item name="requestMode" style={{ marginBottom: 6 }}>
                        <Segmented
                          block={isMobile}
                          disabled={submitting}
                          options={[
                            { label: translate(t, TRANSLATIONS.SERVICE_FORM_REQUEST_MODE_INSTANT), value: 'Instant' },
                            { label: translate(t, TRANSLATIONS.SERVICE_FORM_REQUEST_MODE_QUOTE), value: 'Quote' },
                          ]}
                        />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {translate(t, TRANSLATIONS.SERVICE_FORM_REQUEST_MODE_HINT)}
                      </Text>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="countryCode" rules={[requiredRule]} style={numericFieldItemStyle}>
                          <Select
                            showSearch
                            disabled={submitting}
                            placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_COUNTRY_LABEL)}
                            filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                            options={countryOptions}
                          />
                        </Form.Item>
                        <Form.Item name="hostCurrencyCode" style={numericFieldItemStyle}>
                          <Select
                            showSearch
                            disabled={submitting}
                            placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_HOST_CURRENCY_LABEL)}
                            filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                            options={currencyOptions}
                          />
                        </Form.Item>
                      </div>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_PRICING_MODE_LABEL)}>
                      <Form.Item name="pricingMode" rules={[requiredRule]}>
                        <WizardTileGrid options={PRICING_MODE_OPTIONS} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        {(pricingMode === 'Hourly' || pricingMode === 'Both') && (
                          <Form.Item name="hourlyRate" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField
                              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_HOURLY_RATE_LABEL)}
                              min={0.01}
                              step={0.01}
                              addonAfter={hostCurrencyCode || 'EUR'}
                              disabled={submitting}
                            />
                          </Form.Item>
                        )}
                        {(pricingMode === 'FixedJob' || pricingMode === 'Both') && (
                          <Form.Item name="fixedJobPrice" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField
                              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_FIXED_JOB_PRICE_LABEL)}
                              min={0.01}
                              step={0.01}
                              addonAfter={hostCurrencyCode || 'EUR'}
                              disabled={submitting}
                            />
                          </Form.Item>
                        )}
                      </div>
                    </FieldGroup>

                    <FieldGroup label="">
                      <Form.Item name="depositAmount" style={{ marginBottom: 4 }}>
                        <PillNumberField
                          label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DEPOSIT_AMOUNT_LABEL)}
                          min={0}
                          step={0.01}
                          addonAfter={hostCurrencyCode || 'EUR'}
                          disabled={submitting}
                        />
                      </Form.Item>
                      {selectedCategoryOption?.attachableToMachineryBooking && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DEPOSIT_MACHINERY_NOTE)}
                        </Text>
                      )}
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'extras' &&
                  (savedService ? (
                    <>
                      {selectedCategoryOption?.attachableToMachineryBooking && (
                        <ServiceCompatibilityManager service={savedService} onChange={setSavedService} disabled={submitting} />
                      )}
                      <ServiceAddOnManager service={savedService} onChange={setSavedService} disabled={submitting} />
                    </>
                  ) : (
                    <Spin />
                  ))}
              </motion.div>
            </AnimatePresence>
          </Form>
        </div>

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            paddingLeft: isMobile ? 20 : 56,
            paddingRight: isMobile ? 20 : 56,
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          {phaseIndex > 0 ? (
            <Button shape="round" size="large" onClick={goBack} disabled={submitting}>
              {translate(t, TRANSLATIONS.HOST_ONBOARDING_BACK)}
            </Button>
          ) : (
            <div />
          )}
          {isLastPhase ? (
            <Button shape="round" size="large" type="primary" onClick={() => navigate('/heavy-duty-machinery/services')}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_FINISH_BUTTON)}
            </Button>
          ) : (
            <Button shape="round" size="large" type="primary" onClick={goNext} loading={submitting}>
              {translate(
                t,
                currentPhase.key === 'pricing' ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_SAVE_AND_CONTINUE_BUTTON : TRANSLATIONS.WIZARD_CONTINUE_BUTTON
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
